/**
 * Quality NCR list column configuration.
 *
 * Mirrors the Schedule Lines column-manager pattern (types.ts +
 * load/save + drag reorder). Column set sourced from the legacy
 * SharePoint Corrective/Preventative Action List that customers
 * still expect parity with.
 *
 * The 5 columns marked `dataMissing: true` are intentionally listed
 * with a "—" placeholder render — the underlying NCR model doesn't
 * carry the value yet, but the column is exposed so the customer
 * can see what's expected and request the model addition.
 */
import type { ReactNode } from 'react';
import type { NonConformanceReport } from '../../lib/api/types/quality';

export interface NCRColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  sortable: boolean;
  /** API ordering field name when sortable. Falls back to id. */
  sortField?: string;
  minWidth: string;
  order: number;
  /** True when the spec column has no backing model data — renders as "—". */
  dataMissing?: boolean;
}

export const NCR_COLUMN_STORAGE_KEY = 'quality_ncr_column_config';

/**
 * Default column set — order, visibility, and labels match the
 * Dennis Eagle SharePoint export (`Columns.xlsx`). Visibility off-by-default
 * for less-used columns to keep first-load uncluttered.
 */
export const DEFAULT_NCR_COLUMNS: NCRColumnConfig[] = [
  { id: 'ncr_number', label: 'Title', visible: true, sortable: true, sortField: 'ncr_number', minWidth: '110px', order: 0 },
  { id: 'raised_date', label: 'Created', visible: true, sortable: true, sortField: 'raised_date', minWidth: '140px', order: 1 },
  { id: 'raised_by_name', label: 'Created By', visible: true, sortable: false, minWidth: '160px', order: 2 },
  { id: 'supplier_code', label: 'Supplier Code', visible: false, sortable: false, minWidth: '120px', order: 3 },
  { id: 'supplier_name', label: 'Supplier Name', visible: true, sortable: true, sortField: 'supplier_name', minWidth: '180px', order: 4 },
  { id: 'supplier_country', label: 'UK/EU', visible: false, sortable: false, minWidth: '90px', order: 5 },
  { id: 'part_number', label: 'Part No.', visible: true, sortable: true, sortField: 'part_number', minWidth: '120px', order: 6 },
  { id: 'defect_description', label: 'Defect Description', visible: true, sortable: false, minWidth: '240px', order: 7 },
  { id: 'liability_display', label: 'Liability', visible: true, sortable: false, minWidth: '130px', order: 8 },
  { id: 'proposed_rejection_display', label: 'Action to Resolve', visible: true, sortable: false, minWidth: '160px', order: 9 },
  { id: 'collection_date', label: 'Collection Date', visible: false, sortable: false, minWidth: '130px', order: 10 },
  { id: 'status_display', label: 'Action Status', visible: true, sortable: true, sortField: 'status', minWidth: '120px', order: 11 },
  { id: 'immediate_corrective_action', label: 'Actions Initiated', visible: false, sortable: false, minWidth: '260px', order: 12 },
  { id: 'supplier_contact_email', label: 'Supplier Contact', visible: false, sortable: false, minWidth: '200px', order: 13 },
  { id: 'quality_engineer_feedback', label: 'Quality Engineer', visible: false, sortable: false, minWidth: '180px', order: 14 },
  { id: 'company_name', label: 'DEW/DEB', visible: false, sortable: false, minWidth: '150px', order: 15 },
  { id: 'reject_qty', label: 'Reject Qty', visible: true, sortable: false, minWidth: '90px', order: 16 },
  { id: 'unit_price', label: 'Unit Cost', visible: true, sortable: false, minWidth: '100px', order: 17 },
  { id: 'reject_value', label: 'Total Price', visible: true, sortable: false, minWidth: '110px', order: 18 },
  { id: 'commodity', label: 'Commodity', visible: false, sortable: false, minWidth: '160px', order: 19, dataMissing: true },
  { id: 'cost_of_rework', label: 'Cost of Rework', visible: false, sortable: false, minWidth: '120px', order: 20, dataMissing: true },
  { id: 'is_8d_required', label: "CCAR Req'd", visible: false, sortable: false, minWidth: '100px', order: 21 },
  { id: 'car_required_date', label: 'Date CCAR Raised', visible: false, sortable: false, minWidth: '150px', order: 22 },
  { id: 'ca_submitted_date', label: 'D3→D4 Submitted', visible: false, sortable: false, minWidth: '150px', order: 23 },
  { id: 'car_submitted_date', label: 'D5→D7 Actions', visible: false, sortable: false, minWidth: '140px', order: 24 },
  { id: 'credit_debit_no', label: 'Credit / Debit No.', visible: false, sortable: false, minWidth: '140px', order: 25, dataMissing: true },
  { id: 'debit_note_raised', label: 'Debit Note Raised', visible: false, sortable: false, minWidth: '140px', order: 26, dataMissing: true },
  { id: 'defect_photo_count', label: 'Defect Photo', visible: false, sortable: false, minWidth: '110px', order: 27 },
  { id: 'is_credit_required', label: 'Claim Status', visible: false, sortable: false, minWidth: '120px', order: 28 },
  { id: 'closed_date', label: 'Date Closed', visible: false, sortable: true, sortField: 'closed_date', minWidth: '130px', order: 29 },
  { id: 'last_modified_by_name', label: 'Modified By', visible: false, sortable: false, minWidth: '160px', order: 30 },
  { id: 'latest_supplier_comment', label: 'Supplier Comments', visible: false, sortable: false, minWidth: '260px', order: 31 },
  { id: 'quantity_scrapped', label: 'Scrapped Qty', visible: false, sortable: false, minWidth: '110px', order: 32 },
];

export function loadNCRColumns(): NCRColumnConfig[] {
  try {
    const stored = localStorage.getItem(NCR_COLUMN_STORAGE_KEY);
    if (!stored) return DEFAULT_NCR_COLUMNS;
    const parsed = JSON.parse(stored) as NCRColumnConfig[];
    const storedMap = new Map(parsed.map((c) => [c.id, c]));
    return DEFAULT_NCR_COLUMNS.map((def) => {
      const saved = storedMap.get(def.id);
      return saved ? { ...def, visible: saved.visible, order: saved.order } : def;
    }).sort((a, b) => a.order - b.order);
  } catch {
    return DEFAULT_NCR_COLUMNS;
  }
}

export function saveNCRColumns(cols: NCRColumnConfig[]) {
  localStorage.setItem(NCR_COLUMN_STORAGE_KEY, JSON.stringify(cols));
}

/**
 * Render the cell value for a given column. Returns ReactNode so callers can
 * pass formatters (date, number) without per-row branching at the call site.
 *
 * `dataMissing` columns short-circuit to the dash so the cell remains
 * visually present but obviously empty — pairs with the column-manager
 * dropdown footnote so the customer knows it's expected.
 */
export function renderNCRCell(
  col: NCRColumnConfig,
  ncr: NonConformanceReport,
  helpers: {
    formatDate: (d: string | Date) => string;
    formatNumber: (n: number) => string;
  },
): ReactNode {
  if (col.dataMissing) return <span className="text-muted-foreground/50">&mdash;</span>;
  const dash = <span className="text-muted-foreground/50">&mdash;</span>;
  const val = (ncr as unknown as Record<string, unknown>)[col.id];
  switch (col.id) {
    case 'ncr_number':
      // Title in the spec is always the user-facing NCR number; fall back to
      // a dash rather than the DB primary key so the table never shows
      // a 5-digit internal id where users expect a "70350"-style identifier.
      return ncr.ncr_number || dash;
    case 'raised_date':
      return ncr.raised_date ? helpers.formatDate(ncr.raised_date) : dash;
    case 'closed_date':
      return ncr.closed_date ? helpers.formatDate(ncr.closed_date) : dash;
    case 'collection_date':
      return ncr.collection_date ? helpers.formatDate(ncr.collection_date) : dash;
    case 'car_required_date':
      return ncr.car_required_date ? helpers.formatDate(ncr.car_required_date) : dash;
    case 'ca_submitted_date':
      return ncr.ca_submitted_date ? helpers.formatDate(ncr.ca_submitted_date) : dash;
    case 'car_submitted_date':
      return ncr.car_submitted_date ? helpers.formatDate(ncr.car_submitted_date) : dash;
    case 'reject_qty':
    case 'quantity_scrapped':
    case 'quantity_reworked':
      return typeof val === 'number' ? helpers.formatNumber(val) : dash;
    case 'unit_price':
      return ncr.unit_price ? helpers.formatNumber(parseFloat(ncr.unit_price)) : dash;
    case 'reject_value':
      return typeof ncr.reject_value === 'number' ? helpers.formatNumber(ncr.reject_value) : dash;
    case 'is_8d_required':
      return ncr.is_8d_required ? 'Yes' : 'No';
    case 'is_credit_required':
      // "Claim Status" in the spec is a yes/no flag for whether a credit
      // claim is required — kept distinct from the row's NCR workflow
      // `status` which has its own dedicated column.
      return ncr.is_credit_required ? 'Required' : 'Not Required';
    case 'defect_description':
    case 'immediate_corrective_action':
    case 'latest_supplier_comment':
    case 'quality_engineer_feedback':
    case 'supplier_contact_email': {
      const text = String(val ?? '').trim();
      if (!text) return dash;
      // Truncate long text to keep rows scannable; full value stays in tooltip.
      return <span title={text}>{text.length > 80 ? `${text.slice(0, 78)}…` : text}</span>;
    }
    case 'defect_photo_count': {
      const n = ncr.defect_photo_count ?? 0;
      return n > 0 ? `${n} photo${n === 1 ? '' : 's'}` : dash;
    }
    default:
      if (val === null || val === undefined || val === '') return dash;
      return String(val);
  }
}
